import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Tabs,
  Tab,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Home as HomeIcon,
  Groups as LeagueIcon,
  Person as TeamIcon,
  People as PlayersIcon,
  Assignment as DraftIcon,
  Settings as SettingsIcon,
  EmojiEvents as MatchupIcon,
  BarChart as StatsIcon,
  Menu as MenuIcon,
  Score as ScoreIcon,
  EventNote as ScheduleIcon,
} from '@mui/icons-material';

const versionHistory = [
  {
    version: '0.4.0',
    title: 'Trello board link and backend sync improvements',
    date: 'Wed Dec 10, 2025',
    highlights: [
      'Added Trello board link and email shortcut to submit bugs from the version popup.',
      'Rebuilt backend with improved syncing.',
    ],
  },
  {
    version: '0.3.1',
    title: 'Team management and backend improvements',
    date: 'Wed Dec 10, 2025',
    highlights: [
      'Team renaming functionality added to settings page.',
      'Draft team count bug fix.',
      'Entire backend syncing rework.',
    ],
  },
  {
    version: '0.3.0',
    title: 'Schedules and round-robin updates',
    date: 'Tue Dec 9, 2025',
    highlights: [
      'Fantasy schedules generated and exposed by team; schedule page shows team names.',
      'Matchup page uses scheduled opponents for current round; lighter data fetches elsewhere.',
      'Round-robin scheduling aligned to full NBL season length.',
    ],
  },
  {
    version: '0.2',
    title: 'Settings',
    date: 'Mon Dec 8, 2025',
    highlights: [
      'Settings page added for managing user accounts and team names.',
    ],
  },
  {
    version: '0.1',
    title: 'Initial test version',
    date: 'Sat Dec 6, 2025',
    highlights: [
      'Draft capabilities added for conducting player drafts.',
    ],
  },
];

const CURRENT_VERSION = versionHistory[0].version;

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);

  const navigationItems = [
    { path: '/', label: 'Home', icon: <HomeIcon /> },
    { path: '/league', label: 'League', icon: <LeagueIcon /> },
    { path: '/myteam', label: 'My Team', icon: <TeamIcon /> },
    { path: '/players', label: 'Players', icon: <PlayersIcon /> },
    { path: '/scores', label: 'Scores', icon: <ScoreIcon /> },
    { path: '/draft', label: 'Draft', icon: <DraftIcon /> },
    { path: '/matchup', label: 'Matchup', icon: <StatsIcon /> },
    { path: '/stats', label: 'Standings', icon: <MatchupIcon /> },
    { path: '/schedule', label: 'Schedule', icon: <ScheduleIcon /> },
    { path: '/settings', label: 'Settings', icon: <SettingsIcon /> },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const currentTab = navigationItems.findIndex(item => item.path === location.pathname);

  const drawer = (
    <Box>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Down Under Dynasty
          <Typography
            variant="caption"
            component="span"
            onClick={() => {
              setChangelogOpen(true);
              setMobileOpen(false);
            }}
            sx={{
              cursor: 'pointer',
              opacity: 0.8,
              '&:hover': {
                opacity: 1,
                textDecoration: 'underline',
              },
            }}
          >
            v.{CURRENT_VERSION}
          </Typography>
        </Typography>
      </Toolbar>
      <List>
        {navigationItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={location.pathname === item.path}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="static" elevation={1}>
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={() => setMobileOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            Down Under Dynasty
            <Typography
              variant="caption"
              component="span"
              onClick={() => setChangelogOpen(true)}
              sx={{
                cursor: 'pointer',
                opacity: 0.8,
                '&:hover': {
                  opacity: 1,
                  textDecoration: 'underline',
                },
              }}
            >
            v.{CURRENT_VERSION}
            </Typography>
          </Typography>

          {!isMobile && (
            <Tabs
              value={currentTab >= 0 ? currentTab : false}
              onChange={(_, newValue) => {
                if (newValue !== false) {
                  handleNavigation(navigationItems[newValue].path);
                }
              }}
              sx={{
                '& .MuiTab-root': {
                  minWidth: 'auto',
                  px: 2,
                  color: 'white',
                  '&.Mui-selected': {
                    color: 'white',
                  },
                },
              }}
            >
              {navigationItems.map((item) => (
                <Tab
                  key={item.path}
                  label={item.label}
                  icon={item.icon}
                  iconPosition="top"
                  sx={{ fontSize: '0.75rem' }}
                />
              ))}
            </Tabs>
          )}

        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
        }}
      >
        {drawer}
      </Drawer>

      {/* Changelog Dialog */}
      <Dialog
        open={changelogOpen}
        onClose={() => setChangelogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Down Under Dynasty
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Version {CURRENT_VERSION}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {versionHistory.map((entry) => (
            <Box key={entry.version} sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                v{entry.version} — {entry.title}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {entry.date}
              </Typography>
              <List dense>
                {entry.highlights.map((item, idx) => (
                  <ListItem key={`${entry.version}-${idx}`} sx={{ py: 0 }}>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1, mr: { xs: 0, sm: 1 } }}>
            <Button
              variant="outlined"
              color="primary"
              href="https://trello.com/b/aMteXHKG/down-under-dynasty-bugs"
              target="_blank"
              rel="noopener noreferrer"
            >
              Trello board (features & bugs)
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              href="mailto:evanmason5+sdhnqan5n7wzgp5ilael@app.trello.com?subject=Bug%20report&body=Describe%20the%20bug%20here:"
            >
              Email to submit a bug
            </Button>
          </Box>
          <Button onClick={() => setChangelogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navigation;
